// /home/szymon/kreator2/frontend/src/components/editor/EditorState.js
// v10.1.0 – Dodano loadingPages + usunięto zbędne logi.
// v10.2.0 - Dodano obsługę foldLine

import { useState, useEffect, useRef } from "react";
import { saveProjectToDb } from "../../services/projectApi";
// Importujemy nową funkcję pomocniczą
import { calculateCenteredX } from "./snapUtils";

// Usunięto funkcję autoSnapAllPagesWithSimulation - wraca do Editor.js

// Funkcja do normalizacji subZ na stronie
function normalizePageSubZ(page, index) {
  if (!page) return page;
  console.log(`[normalizePageSubZ] Normalizing page index: ${index}`);

  let allObjects = [
    ...(page.texts || []).map(obj => ({ ...obj, _type: 'text', _originalArray: 'texts', originalSubZ: obj.subZ, originalZGroup: obj.zGroup })),
    ...(page.images || []).map(obj => ({ ...obj, _type: 'image', _originalArray: 'images', originalSubZ: obj.subZ, originalZGroup: obj.zGroup })),
    ...(page.svgs || []).map(obj => ({ ...obj, _type: 'svg', _originalArray: 'svgs', originalSubZ: obj.subZ, originalZGroup: obj.zGroup }))
  ];
  console.log(`[normalizePageSubZ] Page ${index} - All objects collected for normalization:`, allObjects.map(o => ({id: o.id, type: o._type, zGroup: o.originalZGroup, subZ: o.originalSubZ })));


  // Definicja kolejności renderowania na podstawie zGroup (od najniższej do najwyższej)
  // Te wartości powinny odpowiadać temu, jak `zGroup` było używane do ustalania kolejności.
  // Niższa wartość = renderowane niżej (wcześniej).
  const zGroupSortOrder = (obj) => {
    // Domyślne wartości zGroup, jeśli nie zdefiniowane w obiekcie
    const defaultZGroups = { image: 1, svg: 2, text: 3 };
    return obj.originalZGroup ?? defaultZGroups[obj._type] ?? 99; // 99 dla nieznanych
  };

  allObjects.sort((a, b) => {
    const zGroupA = zGroupSortOrder(a);
    const zGroupB = zGroupSortOrder(b);

    if (zGroupA !== zGroupB) {
      return zGroupA - zGroupB;
    }
    // Jeśli zGroup są takie same, sortuj według oryginalnego subZ (jeśli istnieje i jest liczbą)
    // lub zachowaj oryginalną kolejność w tablicy (tutaj uproszczone do braku dalszego sortowania,
    // co oznacza, że kolejność zależy od tego jak były połączone tablice)
    // Dla bardziej stabilnego sortowania można by dodać indeks z oryginalnej tablicy.
    const subZA = typeof a.originalSubZ === 'number' ? a.originalSubZ : Infinity;
    const subZB = typeof b.originalSubZ === 'number' ? b.originalSubZ : Infinity;
    if (subZA !== Infinity && subZB !== Infinity && subZA !== subZB) {
        return subZA - subZB;
    }
    // Jeśli subZ są takie same lub nie istnieją, można dodać dodatkowy stabilny klucz sortowania, np. ID
    // return String(a.id).localeCompare(String(b.id)); // Opcjonalne dla stabilności
    return 0; // Zachowaj względną kolejność, jeśli zGroup i subZ są takie same
  });
  console.log(`[normalizePageSubZ] Page ${index} - Objects sorted by zGroup then originalSubZ:`, allObjects.map(o => ({id: o.id, type: o._type, zGroup: zGroupSortOrder(o), subZ_orig: o.originalSubZ })));

  // Przypisz nowe, sekwencyjne subZ
  allObjects.forEach((obj, newSubZIndex) => {
    obj.subZ = newSubZIndex;
  });
  console.log(`[normalizePageSubZ] Page ${index} - New subZ values assigned:`, allObjects.map(o => ({id: o.id, type: o._type, zGroup: zGroupSortOrder(o), subZ_new: o.subZ })));


  const newPage = {
    ...page,
    texts: allObjects.filter(obj => obj._originalArray === 'texts').map(obj => { const { _type, _originalArray, originalSubZ, originalZGroup, ...rest } = obj; return rest; }),
    images: allObjects.filter(obj => obj._originalArray === 'images').map(obj => { const { _type, _originalArray, originalSubZ, originalZGroup, ...rest } = obj; return rest; }),
    svgs: allObjects.filter(obj => obj._originalArray === 'svgs').map(obj => { const { _type, _originalArray, originalSubZ, originalZGroup, ...rest } = obj; return rest; }),
  };
  
  // Logowanie przykładów subZ po normalizacji
  if (newPage.texts && newPage.texts.length > 0) console.log(`[normalizePageSubZ] Page ${index} - Example text subZ after normalization:`, newPage.texts[0].id, newPage.texts[0].subZ);
  if (newPage.images && newPage.images.length > 0) console.log(`[normalizePageSubZ] Page ${index} - Example image subZ after normalization:`, newPage.images[0].id, newPage.images[0].subZ);
  if (newPage.svgs && newPage.svgs.length > 0) console.log(`[normalizePageSubZ] Page ${index} - Example svg subZ after normalization:`, newPage.svgs[0].id, newPage.svgs[0].subZ);

  return newPage;
}


// Uniwersalna funkcja do zapisu w DB (admin / client).
async function handleSaveToDb({
  projectId,
  downloadKey,
  jsonData,
  saveType,
  backendUrl,
  isPublicMode,
  publicMethodUsed, // "key" / "id" / null => decyduje ścieżkę w trybie public
  isTemplateEditor, // Dodano flagę isTemplateEditor
  options = {} // Dodano parametr options dla dodatkowych opcji
}) {
  // Minimalny log, do debug ewentualnego
  console.log("[EditorState] handleSaveToDb =>", { 
    projectId, 
    downloadKey, 
    saveType, 
    isTemplateEditor, 
    isPublicMode,
    autoCentered: options.autoCentered, // Dodano log dla autoCentered
    foldLine: options.foldLine // Dodano log dla foldLine
  });

  let url;
  if (!isPublicMode) {
    // Tryb admin: rozróżnij zapis szablonu od projektu
    if (isTemplateEditor) {
      url = `${backendUrl}/admin/templates/${projectId}/history`;
      console.log(`[EditorState] handleSaveToDb: Using TEMPLATE save URL: ${url}`);
    } else {
      url = `${backendUrl}/admin/projects/${projectId}/history`;
      console.log(`[EditorState] handleSaveToDb: Using PROJECT save URL: ${url}`);
    }
  } else {
    // Tryb public (zawsze dotyczy projektów, nie szablonów)
    if (publicMethodUsed === "key") {
      url = `${backendUrl}/client/projects-key/${downloadKey}/history`;
    } else {
      url = `${backendUrl}/client/projects/${projectId}/history`;
    }
  }

  // Dodajemy flagę autoCentered i foldLine do danych zapisu, jeśli zostały przekazane
  const saveData = {
    url,
    jsonData,
    saveType,
    autoCentered: options.autoCentered || false, // Domyślnie false, jeśli nie podano
    foldLine: options.foldLine || null // Przekazujemy foldLine
  };

  return saveProjectToDb(saveData);
}

// Funkcja pomocnicza do konwersji lokalnej ścieżki na URL
function convertLocalPathToUrl(localPath, backendUrl, isPublicMode) {
  if (!localPath || typeof localPath !== 'string') {
    return localPath; // Zwróć oryginalną wartość, jeśli nie jest to poprawna ścieżka
  }
  const basePath = "/home/szymon/kreator2/backend/uploads/";
  let relativePath = localPath;
  if (localPath.startsWith(basePath)) {
    relativePath = localPath.substring(basePath.length);
  }
  // Usuń potencjalne znaki specjalne z początku ścieżki, jeśli nie jest to część 'images/' lub 'fonts/'
  if (!relativePath.startsWith('images/') && !relativePath.startsWith('fonts/')) {
      relativePath = relativePath.replace(/^[^a-zA-Z0-9]+/, '');
  }

  const prefixRaw = isPublicMode
    ? `${backendUrl}/client/raw-file?path=`
    : `${backendUrl}/admin/raw-file?path=`;

  // Upewnij się, że backendUrl nie ma końcowego slasha, a ścieżka nie ma początkowego
  const cleanBackendUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
  const cleanRelativePath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;

  const rawFileEndpoint = isPublicMode ? '/client/raw-file' : '/admin/raw-file';

  // Zwróć pełny URL
  // Przykład: http://localhost:6001/admin/raw-file?path=images%2Fmy-image.svg
  return `${cleanBackendUrl}${rawFileEndpoint}?path=${encodeURIComponent(cleanRelativePath)}`;
}


/**
 * parseOnePage:
 *  - baseWidth/baseHeight -> fallback: pageWidth/pageHeight -> fallback: 800×600
 */
function parseOnePage(pageData) {
  let w = pageData.baseWidth;
  let h = pageData.baseHeight;

  if (typeof w !== "number" || typeof h !== "number") {
    w = pageData.pageWidth;
    h = pageData.pageHeight;
  }
  if (typeof w !== "number" || isNaN(w)) w = 800;
  if (typeof h !== "number" || isNaN(h)) h = 600;

  // Process items array if it exists (for templates)
  const texts = [];
  const images = [];
  const svgs = [];
  if (Array.isArray(pageData.items)) {
    pageData.items.forEach(item => {
      // Convert ID to string for Konva compatibility
      // Extract x, y from bbox
      const x = item.bbox ? item.bbox[0] : 0;
      const y = item.bbox ? item.bbox[1] : 0;
      // --- ADD LOGGING ---
      console.log(`[parseOnePage] Item ID: ${item.id}, Type: ${item.type}, BBox: ${JSON.stringify(item.bbox)}, Extracted X: ${x}, Extracted Y: ${y}`);
      // --- END LOGGING ---
      const stringIdItem = { ...item, id: String(item.id), x: x, y: y };

      if (item.type === 'text') {
        texts.push(stringIdItem);
      } else if (item.type === 'image') {
        // Ensure imagePath is converted to src if necessary
        // Use width/height from item if available, otherwise calculate from bbox (less common for images here)
        const width = item.width || (item.bbox ? item.bbox[2] - item.bbox[0] : undefined);
        const height = item.height || (item.bbox ? item.bbox[3] - item.bbox[1] : undefined);
        images.push({ ...stringIdItem, src: item.imagePath || item.src, width, height });
      } else if (item.type === 'vectorSVG') { // Poprawiono warunek na 'vectorSVG'
         const width = item.width || (item.bbox ? item.bbox[2] - item.bbox[0] : undefined);
         const height = item.height || (item.bbox ? item.bbox[3] - item.bbox[1] : undefined);
        svgs.push({ ...stringIdItem, src: item.svgPath || item.src, width, height }); // Używamy svgPath jako źródła
      }
    });
  } else {
    // Fallback to direct arrays (for projects/orders) - ensure IDs are strings and x/y are present
    (pageData.texts || []).forEach(item => texts.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
    (pageData.images || []).forEach(item => images.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
    (pageData.svgs || []).forEach(item => svgs.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
  }
  // Removed duplicated fallback block and extra closing brace

  return {
    baseWidth: w,
    baseHeight: h,
    texts: texts,
    images: images,
    svgs: svgs,
    guides: pageData.guides || [],
    personalizacja: pageData.personalizacja || [],
    pageIndex: pageData.pageIndex ?? 0
  };
}

export function useEditorState({
  backendUrl = "http://localhost:6001",
  pdfData = null,
  projectId = null, // Może być ID projektu lub ID pliku szablonu
  downloadKey = null,
  isAccepted = false,
  isTemplateEditor = false // Nowa flaga wskazująca, czy edytujemy szablon
}) {
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [publicMode, setPublicMode] = useState(false);
  const [publicMethodUsed, setPublicMethodUsed] = useState(null); // "key"|"id"|null

  const [fontsAreLoaded, setFontsAreLoaded] = useState(false);
  const [availableFonts, setAvailableFonts] = useState([]);

  const [assetsAreLoaded, setAssetsAreLoaded] = useState(false);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsCount, setAssetsCount] = useState(0);

  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [panelMode, setPanelMode] = useState("projectSupport");
  const [editingTextId, setEditingTextId] = useState(null);

  const [forceRefresh, setForceRefresh] = useState(0);
  const changesCountRef = useRef(0);

  const [hasLoadedFromDb, setHasLoadedFromDb] = useState(false); // Czy dane pochodzą z DB?
  const [loadingPages, setLoadingPages] = useState(true); // Start loading initially
  const [initialAutoCentered, setInitialAutoCentered] = useState(false); // Nowy stan
  const [foldLineConfig, setFoldLineConfig] = useState(null); // Nowy stan dla linii zgięcia
  const [aiPreviousVersionId, setAiPreviousVersionId] = useState(null);
  // Usunięto initialSetupDone - nie jest już potrzebny

  const initialCenteringAppliedRef = useRef(false); // Nowy ref do śledzenia centrowania
  const initialLoadEffectRan = useRef(false); // Ref do śledzenia, czy główny efekt ładowania już się wykonał

  // ============ Auto-zapis =============
  function doAutoSave(customPages) {
    if (publicMode && isAccepted) return; // brak zapisu w public mode po akceptacji

    const usedPages = customPages || pages;
    // Głęboka kopia, aby nie modyfikować oryginalnego stanu `pages`
    const pagesToSave = JSON.parse(JSON.stringify(usedPages));

    // Konwertuj ścieżki SVG na URL przed zapisem
    pagesToSave.forEach(page => {
      if (page.svgs && Array.isArray(page.svgs)) {
        page.svgs = page.svgs.map(svg => {
          // Sprawdź, czy src wygląda jak lokalna ścieżka przed konwersją
          if (svg.src && typeof svg.src === 'string' && svg.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
             console.log(`[EditorState doAutoSave] Converting SVG path to URL: ${svg.src}`);
             const urlSrc = convertLocalPathToUrl(svg.src, backendUrl, publicMode);
             console.log(`[EditorState doAutoSave] Converted URL: ${urlSrc}`);
             return { ...svg, src: urlSrc };
          }
          return svg; // Zwróć bez zmian, jeśli src nie jest lokalną ścieżką
        });
      }
       // Podobnie dla obrazków, jeśli używają lokalnych ścieżek
       if (page.images && Array.isArray(page.images)) {
        page.images = page.images.map(img => {
          if (img.src && typeof img.src === 'string' && img.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
            console.log(`[EditorState doAutoSave] Converting Image path to URL: ${img.src}`);
            const urlSrc = convertLocalPathToUrl(img.src, backendUrl, publicMode);
            console.log(`[EditorState doAutoSave] Converted URL: ${urlSrc}`);
            return { ...img, src: urlSrc };
          }
          return img;
        });
      }
    });


    const final = pagesToSave.map((pg, idx) => ({
      ...pg,
      pageIndex: pg.pageIndex ?? idx
    }));
    const finalJson = { pages: final };
    if (downloadKey) finalJson.downloadKey = downloadKey;

    handleSaveToDb({
      projectId,
      downloadKey,
      jsonData: finalJson,
      saveType: "autosave",
      backendUrl,
      isPublicMode: publicMode,
      publicMethodUsed,
      isTemplateEditor, // Przekaż flagę
      options: { foldLine: foldLineConfig } // Przekazujemy foldLineConfig
    }).catch((err) => { console.error("Autosave failed:", err); });
    // *** NOWY LOG: Sprawdź zawartość svgs przed zapisem ***
    console.log('[EditorState doAutoSave] finalJson to be saved:', JSON.stringify(finalJson, null, 2));
    // *** KONIEC NOWEGO LOGU ***
    changesCountRef.current = 0;
  }

  // ============ Ręczny zapis =============
  function handleManualSave(customPages, options = {}) {
    if (publicMode && isAccepted) return Promise.reject(new Error("Nie można zapisać w trybie publicznym po akceptacji"));

    const usedPages = customPages || pages;
    // Głęboka kopia, aby nie modyfikować oryginalnego stanu `pages`
    const pagesToSave = JSON.parse(JSON.stringify(usedPages));

     // Konwertuj ścieżki SVG na URL przed zapisem
    pagesToSave.forEach(page => {
      if (page.svgs && Array.isArray(page.svgs)) {
        page.svgs = page.svgs.map(svg => {
          // Sprawdź, czy src wygląda jak lokalna ścieżka przed konwersją
          if (svg.src && typeof svg.src === 'string' && svg.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
             console.log(`[EditorState handleManualSave] Converting SVG path to URL: ${svg.src}`);
             const urlSrc = convertLocalPathToUrl(svg.src, backendUrl, publicMode);
             console.log(`[EditorState handleManualSave] Converted URL: ${urlSrc}`);
             return { ...svg, src: urlSrc };
          }
          return svg; // Zwróć bez zmian, jeśli src nie jest lokalną ścieżką
        });
      }
       // Podobnie dla obrazków, jeśli używają lokalnych ścieżek
       if (page.images && Array.isArray(page.images)) {
        page.images = page.images.map(img => {
          if (img.src && typeof img.src === 'string' && img.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
            console.log(`[EditorState handleManualSave] Converting Image path to URL: ${img.src}`);
            const urlSrc = convertLocalPathToUrl(img.src, backendUrl, publicMode);
            console.log(`[EditorState handleManualSave] Converted URL: ${urlSrc}`);
            return { ...img, src: urlSrc };
          }
          return img;
        });
      }
    });

    const final = pagesToSave.map((pg, idx) => ({
      ...pg,
      pageIndex: pg.pageIndex ?? idx
    }));
    const finalJson = { pages: final };
    if (downloadKey) finalJson.downloadKey = downloadKey;

    // Dodajemy log dla opcji
    console.log('[EditorState handleManualSave] Options:', options);

    // Zwracamy Promise, aby można było czekać na zakończenie zapisu
    return handleSaveToDb({
      projectId,
      downloadKey,
      jsonData: finalJson,
      saveType: "manual",
      backendUrl,
      isPublicMode: publicMode,
      publicMethodUsed,
      isTemplateEditor, // Przekaż flagę
      options: { ...options, foldLine: foldLineConfig } // Łączymy istniejące opcje z foldLineConfig
    }).catch((err) => { 
      console.error("Manual save failed:", err);
      throw err; // Przekazujemy błąd dalej
    });
  }


  // Combined Effect for Initial Load (History or pdfData Fallback)
  useEffect(() => {
    if (initialLoadEffectRan.current && !projectId && !downloadKey) {
    }

    console.log("[EditorState] Initial Load Effect Triggered", { projectId, downloadKey, isTemplateEditor, pdfDataPublicMode: pdfData?.publicMode });
    initialLoadEffectRan.current = true; 

    const isCurrentlyPublic = !!pdfData?.publicMode; 
    console.log(`[EditorState] Determined mode for this run: isCurrentlyPublic = ${isCurrentlyPublic}`);

    setPublicMode(isCurrentlyPublic);

    let initialPagesFromPdfData = null;
    if (pdfData) {
        let pagesDataToParse = null;
        if (Array.isArray(pdfData?.pdfMetadatas) && pdfData.pdfMetadatas.length > 0 && pdfData.pdfMetadatas[0]?.metadata?.pages) {
            pagesDataToParse = pdfData.pdfMetadatas[0].metadata.pages;
        } else if (Array.isArray(pdfData?.pages) && pdfData.pages.length > 0) {
            pagesDataToParse = pdfData.pages;
        }
        if (pagesDataToParse) {
            initialPagesFromPdfData = pagesDataToParse.map((pg, i) => {
                if (pg.pageIndex === undefined) pg.pageIndex = i;
                return parseOnePage(pg); 
            });
            console.log("[EditorState] Parsed initial pdfData for potential fallback.");
        } else {
             console.log("[EditorState] pdfData provided, but no valid pages structure found inside.");
        }
    } else {
         console.log("[EditorState] No initial pdfData provided.");
    }

    const loadHistory = async () => {
        const cleanedProjectId = projectId ? String(projectId).replace(/[^0-9]/g, '') : null;
        const cleanedDownloadKey = downloadKey ? String(downloadKey).replace(/[^a-zA-Z0-9]/g, '') : null;

        if (!cleanedProjectId && !cleanedDownloadKey) {
            console.log("[EditorState] No valid projectId or downloadKey for loading history.");
            return null;
        }

        if (window.preventProjectReload) {
            console.log("[EditorState] Skipping history load (preventProjectReload=true)");
            return null;
        }

        setLoadingPages(true);
        let loadedData = null;
        let done = false;
        let currentPublicMethodUsed = null; 

        if (isCurrentlyPublic && cleanedDownloadKey && !done) {
            const urlKey = `${backendUrl}/client/projects-key/${cleanedDownloadKey}/history/latest`;
            console.log(`[EditorState] Attempting load via key (isCurrentlyPublic=${isCurrentlyPublic}): ${urlKey}`);
            try {
                const r1 = await fetch(urlKey, { credentials: "include" });
                if (r1.ok) {
                    const d1 = await r1.json();
                    if (d1 && d1.jsonData && Array.isArray(d1.jsonData.pages)) {
                        loadedData = d1; 
                        currentPublicMethodUsed = "key";
                        done = true;
                        console.log("[EditorState] Load via key successful.");
                    } else { console.log("[EditorState] Load via key OK, but no valid jsonData.pages array found."); }
                } else { console.log(`[EditorState] Load via key failed, status: ${r1.status}`); }
            } catch (err) { console.error("[EditorState] Load via key fetch error:", err); }
        }

        if (!done && isCurrentlyPublic && cleanedProjectId) {
            const urlPublicId = `${backendUrl}/client/projects/${cleanedProjectId}/history/latest`;
            console.log(`[EditorState] Attempting load via public id (isCurrentlyPublic=${isCurrentlyPublic}): ${urlPublicId}`);
            try {
                const r2 = await fetch(urlPublicId, { credentials: "include" });
                if (r2.ok) {
                    const d2 = await r2.json();
                    if (d2 && d2.jsonData && Array.isArray(d2.jsonData.pages)) {
                        loadedData = d2; 
                        currentPublicMethodUsed = "id";
                        done = true;
                        console.log("[EditorState] Load via public id successful.");
                    } else { console.log("[EditorState] Load via public id OK, but no valid jsonData.pages array found."); }
                } else { console.log(`[EditorState] Load via public id failed, status: ${r2.status}`); }
            } catch (err) { console.error("[EditorState] Load via public id fetch error:", err); }
        }

        if (!done && !isCurrentlyPublic && cleanedProjectId) {
            const endpointPath = isTemplateEditor ? `/admin/templates/${cleanedProjectId}/history/latest` : `/admin/projects/${cleanedProjectId}/history/latest`;
            const urlAdmin = `${backendUrl}${endpointPath}`;
            console.log(`[EditorState] Attempting load via admin id (isCurrentlyPublic=${isCurrentlyPublic}, isTemplateEditor=${isTemplateEditor}): ${urlAdmin}`);
            try {
                const r3 = await fetch(urlAdmin, { credentials: "include" });
                if (r3.ok) {
                    const d3 = await r3.json();
                    if (d3 && d3.jsonData && Array.isArray(d3.jsonData.pages)) {
                        loadedData = d3; 
                        currentPublicMethodUsed = null;
                        done = true;
                        console.log(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) successful.`);
                    } else { console.log(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) OK, but no valid jsonData.pages array found.`); }
                } else { console.log(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) failed, status: ${r3.status}`); }
            } catch (err) { console.error(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) fetch error:`, err); }
        }

        console.log("[EditorState] Fetching history completed.", { loadedData: !!loadedData, done });
        setLoadingPages(false);
        setPublicMethodUsed(currentPublicMethodUsed);
        return loadedData; 
    };

    loadHistory().then(historyData => {
        let pagesToSet;
        console.log("[EditorState] Raw historyData received from loadHistory():", JSON.stringify(historyData, null, 2)); // DODATKOWY LOG

        if (historyData && historyData.jsonData && Array.isArray(historyData.jsonData.pages)) {
            const parsedPagesFromDb = historyData.jsonData.pages.map(parseOnePage);
            pagesToSet = parsedPagesFromDb.map((page, idx) => normalizePageSubZ(page, idx)); 
            const loadSource = publicMethodUsed ? `key/id ${publicMethodUsed}` : (isTemplateEditor ? 'template history' : 'project history');
            console.log(`[EditorState] Setting pages from DB (${loadSource}), normalized.`);
            setHasLoadedFromDb(true);
            console.log("[EditorState] Sprawdzanie flagi autoCentered i foldLine. Klucze w historyData:", Object.keys(historyData));
            
            // Ustawianie initialAutoCentered
            if (historyData.jsonData && historyData.jsonData.hasOwnProperty('autoCentered')) {
              console.log(`[EditorState] Ustawianie initialAutoCentered z historii (jsonData.autoCentered): ${historyData.jsonData.autoCentered}`);
              setInitialAutoCentered(!!historyData.jsonData.autoCentered);
            } else if (historyData.hasOwnProperty('autoCentered')) { // Fallback do historyData.autoCentered
              console.log(`[EditorState] Ustawianie initialAutoCentered z historii (historyData.autoCentered): ${historyData.autoCentered}`);
              setInitialAutoCentered(!!historyData.autoCentered);
            } else {
              console.log("[EditorState] Brak flagi autoCentered w historii, ustawianie initialAutoCentered na false.");
              setInitialAutoCentered(false);
            }

            // Ustawianie foldLineConfig
            console.log(`[EditorState] Attempting to set foldLineConfig. isCurrentlyPublic: ${isCurrentlyPublic}`); // DODATKOWY LOG
            if (isCurrentlyPublic) {
              console.log(`[EditorState PUBLIC MODE] Sprawdzanie foldLine w historyData. Klucze: ${Object.keys(historyData).join(', ')}. Wartość historyData.foldLine:`, historyData.foldLine, "Typ:", typeof historyData.foldLine); // DODATKOWY LOG
            }
            
            if (historyData && historyData.hasOwnProperty('foldLine')) { // Sprawdzamy czy historyData nie jest null/undefined
              console.log(`[EditorState] Ustawianie foldLineConfig z historii (wartość historyData.foldLine):`, historyData.foldLine);
              setFoldLineConfig(historyData.foldLine || null);
            } else {
              console.log("[EditorState] Brak klucza 'foldLine' w historyData lub historyData jest null/undefined. Ustawianie foldLineConfig na null."); // Zaktualizowany log
              setFoldLineConfig(null);
            }

            // Ustawianie aiPreviousVersionId
            console.log("[EditorState] Cały obiekt historyData:", historyData);
            if (historyData?.jsonData?.aiPreviousVersionId) {
              console.log(`[EditorState] Ustawianie aiPreviousVersionId z historii: ${historyData.jsonData.aiPreviousVersionId}`);
              setAiPreviousVersionId(historyData.jsonData.aiPreviousVersionId);
            } else {
              console.log("[EditorState] Brak aiPreviousVersionId w historii.");
              setAiPreviousVersionId(null);
            }

        } else {
            console.log("[EditorState] historyData is null, or jsonData or jsonData.pages is missing/invalid. Full historyData:", JSON.stringify(historyData, null, 2)); // DODATKOWY LOG
            if (initialPagesFromPdfData) {
                pagesToSet = initialPagesFromPdfData.map((page, idx) => normalizePageSubZ(page, idx)); 
                console.log("[EditorState] Setting pages from initial pdfData (fallback), normalized.");
            } else {
                console.log("[EditorState] No DB history and no valid pdfData fallback. Setting empty pages.");
                pagesToSet = [];
            }
            setHasLoadedFromDb(false);
            setInitialAutoCentered(false); 
            setFoldLineConfig(null); // Reset foldLineConfig jeśli nie ma danych z historii
        }
        setPages(pagesToSet);
    }).catch(error => {
        console.error("[EditorState] Error during history load/processing:", error);
        let pagesToSetOnError;
        if (initialPagesFromPdfData) {
            pagesToSetOnError = initialPagesFromPdfData.map((page, idx) => normalizePageSubZ(page, idx)); 
            console.log("[EditorState] Setting pages from initial pdfData (error fallback), normalized.");
        } else {
             console.log("[EditorState] No history and no pdfData fallback on error. Setting empty pages.");
            pagesToSetOnError = [];
        }
        setPages(pagesToSetOnError);
        setHasLoadedFromDb(false);
        setLoadingPages(false);
        setInitialAutoCentered(false);
        setFoldLineConfig(null); // Reset foldLineConfig w przypadku błędu
    });

  }, [projectId, downloadKey, backendUrl, pdfData, isTemplateEditor]);

  useEffect(() => {
    let isMounted = true;
    (async function loadAllFonts() {
      try {
        const fontsUrl = publicMode
          ? `${backendUrl}/client/fonts`
          : `${backendUrl}/admin/fonts`;
        const resp = await fetch(fontsUrl, { credentials: "include" });
        if (!resp.ok) throw new Error("font error");
        const list = await resp.json();
        const prefixRaw = publicMode
          ? `${backendUrl}/client/raw-file?path=`
          : `${backendUrl}/admin/raw-file?path=`;

        const tasks = list.map(async (f) => {
          let pathF = f.filePath || "";
          const prefix = "/home/szymon/kreator2/backend/uploads/";
          if (pathF.startsWith(prefix)) {
            pathF = pathF.slice(prefix.length);
          }
          if (!pathF.includes("/")) {
            pathF = "fonts/" + pathF;
          }
          pathF = pathF.replace(/[()]/g, "").replace(/\s/g, "");
          const finalUrl = prefixRaw + encodeURIComponent(pathF);

          try {
            const ff = new FontFace(f.name, `url(${finalUrl})`);
            await ff.load();
            document.fonts.add(ff);
          } catch {}
        });
        await Promise.allSettled(tasks);
        if (isMounted) {
          setAvailableFonts(list);
          setFontsAreLoaded(true);
        }
      } catch {
        if (isMounted) setFontsAreLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [backendUrl, publicMode]);

  useEffect(() => {
    if (pages.length === 0) {
      setAssetsAreLoaded(true);
      return;
    }
    let total = 0;
    let loaded = 0;
    pages.forEach((pg) => {
      total += (pg.images || []).length;
      total += (pg.svgs || []).length;
    });
    if (total === 0) {
      setAssetsAreLoaded(true);
      setAssetsCount(0);
      setAssetsTotal(0);
      return;
    }
    setAssetsTotal(total);
    setAssetsCount(0);

    pages.forEach((pg) => {
      (pg.images || []).forEach((img) => {
        const iObj = new Image();
        iObj.crossOrigin = "anonymous";
        iObj.onload = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        iObj.onerror = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        iObj.src = img.src || "";
      });
      (pg.svgs || []).forEach((sv) => {
        const sObj = new Image();
        sObj.crossOrigin = "anonymous";
        sObj.onload = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        sObj.onerror = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        sObj.src = sv.src || "";
      });
    });
  }, [pages]);

  const lastGuideAddTimeRef = useRef(0);
  const addGuideCallCountRef = useRef(0);
  const lastAddedGuideRef = useRef({ timestamp: 0, id: null });

  function handleAddGuide({ orientation, expression }) {
    const callCount = ++addGuideCallCountRef.current;
    console.log(`[EditorState] handleAddGuide wywołane (${callCount}): `, { orientation, expression });
    console.log(`[EditorState] Stack trace dla wywołania ${callCount}:`, new Error().stack);

    if (publicMode && isAccepted) {
      console.log(`[EditorState] Przerwano dodawanie prowadnicy (${callCount}) - tryb publiczny i zaakceptowane`);
      return;
    }

    const now = Date.now();
    if (now - lastGuideAddTimeRef.current < 500) {
      console.log(`[EditorState] Zablokowano dodanie prowadnicy (${callCount}) - zbyt szybko po poprzedniej (${now - lastGuideAddTimeRef.current}ms)`);
      return; 
    }

    lastGuideAddTimeRef.current = now;
    console.log(`[EditorState] Aktualizowano lastGuideAddTimeRef na ${now}`);

    const uniqueId = "guide-" + now + "-" + Math.floor(Math.random() * 1000);
    console.log(`[EditorState] Wygenerowano uniqueId: ${uniqueId}`);

    lastAddedGuideRef.current = { timestamp: now, id: uniqueId };

    setPages((prev) => {
      console.log(`[EditorState] setPages wywołane dla prowadnicy (${callCount})`);
      const currentGuides = prev[currentPage]?.guides || [];
      const duplicateGuide = currentGuides.find(g =>
        g.orientation === orientation &&
        g.expression === expression &&
        g.id.startsWith(`guide-${Math.floor(now / 1000)}`)
      );

      if (duplicateGuide) {
        console.log(`[EditorState] Wykryto duplikat prowadnicy: ${duplicateGuide.id}, pomijam dodawanie`);
        return prev; 
      }

      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) {
        console.log(`[EditorState] Nieprawidłowy indeks strony: ${currentPage}`);
        return arr;
      }

      const pCopy = { ...arr[currentPage] };
      const existingGuides = pCopy.guides || []; 
      pCopy.guides = [...existingGuides, { 
        id: uniqueId,
        orientation,
        expression
      }];

      console.log(`[EditorState] Dodano prowadnicę (${callCount}), aktualna liczba prowadnic: ${pCopy.guides.length}`);
      console.log(`[EditorState] Lista prowadnic po dodaniu:`, pCopy.guides);

      arr[currentPage] = pCopy;
      return arr;
    });

    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      console.log(`[EditorState] Wykonuję doAutoSave() po dodaniu prowadnicy (${callCount})`);
      doAutoSave();
      changesCountRef.current = 0;
    }

    console.log(`[EditorState] Zakończono handleAddGuide (${callCount})`);
  }
  function handleRemoveGuide(guideId) {
    if (publicMode && isAccepted) return;
    setPages((prev) => {
      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) return arr;
      const pCopy = { ...arr[currentPage] };
      if (!pCopy.guides) pCopy.guides = [];
      pCopy.guides = pCopy.guides.filter((g) => g.id !== guideId);
      arr[currentPage] = pCopy;
      return arr;
    });
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
  }
  function handleChangeGuide(guideId, newExpression) {
    if (publicMode && isAccepted) return;
    setPages((prev) => {
      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) return arr;
      const pCopy = { ...arr[currentPage] };
      if (!pCopy.guides) pCopy.guides = [];
      pCopy.guides = pCopy.guides.map((g) =>
        g.id === guideId ? { ...g, expression: newExpression } : g
      );
      arr[currentPage] = pCopy;
      return arr;
    });
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
  }

  return {
    pages,
    setPages,
    currentPage,
    setCurrentPage,
    hasLoadedFromDb,
    loadingPages,
    fontsAreLoaded,
    availableFonts,
    assetsAreLoaded,
    assetsTotal,
    assetsCount,
    selectedShapeId,
    setSelectedShapeId,
    selectedObject,
    setSelectedObject,
    panelMode,
    setPanelMode,
    editingTextId,
    setEditingTextId,
    forceRefresh,
    doAutoSave,
    handleManualSave,
    changesCountRef,
    publicMode,
    publicMethodUsed,
    handleAddGuide,
    handleRemoveGuide,
    handleChangeGuide,
    triggerForceRefresh: () => setForceRefresh(p => p + 1),
    initialAutoCentered,
    foldLineConfig, // Zwracamy nowy stan
    setFoldLineConfig, // Zwracamy funkcję do aktualizacji stanu linii zgięcia
    aiPreviousVersionId
  };
}
